import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, AlertTriangle, X } from 'lucide-react';
import PlayerRow from '../components/PlayerRow';
import {
  listAllGolfCourses,
  listTeeTimes,
  getSetting,
  updateTeeTime,
  createTeeTime,
  listBookings,
  createBooking,
  updateBooking,
  archiveBooking,
  cancelBooking,
  recheckBooking,
  approveBooking,
  isSupervisorOrAdmin,
  listBlocks,
  createBlock,
  updateBlock,
  archiveBlock,
  listBlockTypes,
} from '../api';
import styles from './CalendarPage.module.css';
import HolePill from '../components/HolePill';
import ArchiveButton from '../components/ArchiveButton/ArchiveButton';
import { computeEstArrival, parseISOTime, formatHourMin } from '../utils';
import BookingInfoPanel from '../components/BookingInfoPanel/BookingInfoPanel';
import { useToasts } from '../hooks/useToasts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const START_HOUR = 5;     // 5 AM
const END_HOUR = 20;      // 8 PM
const BASE_INTERVAL_H = 20; // px per gap-interval in normal view
const MIN_ROW_H = 20;       // px minimum row in compressed view
const DEFAULT_GAP = 10;     // minutes, fallback when API call fails
const DRAG_THRESHOLD_MS = 200; // click vs drag distinction
const TEE_LEFT = 4;         // px from left edge of event area
const TEE_PCT = 20;         // % width for tee time column
const BOOKING_LEFT_PCT = 21; // % from left where bookings start
const BOOKING_RIGHT_PCT = 99; // % from left where bookings end
const BOOKING_AREA_PCT = BOOKING_RIGHT_PCT - BOOKING_LEFT_PCT; // 78%

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function toDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateLong(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Compute estimated hole-10 arrival time string for an 18-hole player on a
 *  Hole 1 tee time.  Returns a formatted time string (e.g. "7:00 AM") or null
 *  if the player/tee-time conditions don't qualify. */

/** Build ISO datetime string for PUT body: combine currentDate string + H:M */
function buildISODatetime(dateStr, hour, minute) {
  const h = String(hour).padStart(2, '0');
  const m = String(minute).padStart(2, '0');
  return `${dateStr}T${h}:${m}:00`;
}

// ---------------------------------------------------------------------------
// Adaptive layout engine
// ---------------------------------------------------------------------------

function buildLayout(teeTimes, gapMins) {
  if (!teeTimes || teeTimes.length === 0) {
    return {
      compressed: false,
      intervalH: BASE_INTERVAL_H,
      intervalMins: gapMins,
      totalH: Math.ceil(((END_HOUR - START_HOUR) * 60) / gapMins) * BASE_INTERVAL_H,
    };
  }

  const sorted = [...teeTimes].sort((a, b) => {
    const ta = parseISOTime(a.start_time);
    const tb = parseISOTime(b.start_time);
    return (ta.hour * 60 + ta.min) - (tb.hour * 60 + tb.min);
  });

  let minGap = Infinity;
  for (let i = 1; i < sorted.length; i++) {
    const prev = parseISOTime(sorted[i - 1].start_time);
    const curr = parseISOTime(sorted[i].start_time);
    const diff = (curr.hour * 60 + curr.min) - (prev.hour * 60 + prev.min);
    if (diff > 0 && diff < minGap) minGap = diff;
  }
  if (minGap === Infinity) minGap = gapMins;

  const compressed = minGap < gapMins;
  const intervalH = compressed
    ? Math.max(MIN_ROW_H, Math.floor((minGap / gapMins) * BASE_INTERVAL_H))
    : BASE_INTERVAL_H;
  const intervalMins = compressed ? minGap : gapMins;
  const totalIntervals = Math.ceil(((END_HOUR - START_HOUR) * 60) / intervalMins);
  const totalH = totalIntervals * intervalH;

  return { compressed, intervalH, intervalMins, totalH, minGap };
}

function timeToY(hour, minute, intervalMins, intervalH) {
  return ((hour - START_HOUR) * 60 + minute) / intervalMins * intervalH;
}

function yToTime(y, intervalMins, intervalH) {
  const totalMins = (y / intervalH) * intervalMins + START_HOUR * 60;
  const snapped = Math.round(totalMins / intervalMins) * intervalMins;
  const hour = Math.floor(snapped / 60);
  const minute = snapped % 60;
  return { hour, minute };
}

// ---------------------------------------------------------------------------
// ModalShell — shared overlay + container + header for all modals
// ---------------------------------------------------------------------------

function ModalShell({ title, size = 'normal', onClose, zIndex, children }) {
  const handleOverlayClick = useCallback(
    (e) => { if (e.target === e.currentTarget) onClose(); },
    [onClose]
  );
  const overlayStyle = zIndex != null ? { zIndex } : undefined;
  return createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
      onClick={handleOverlayClick}
      style={overlayStyle}
    >
      <div className={size === 'wide' ? styles.modalWide : styles.modal}>
        <div className={styles.modalHeader}>
          <p className={styles.modalTitle}>{title}</p>
          <button className={styles.modalCloseBtn} onClick={onClose} aria-label="Close">
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// NewBookingModal
// ---------------------------------------------------------------------------

function NewBookingModal({ teeTime, selectedCourse, onClose, onCreated }) {
  const { hour, min } = parseISOTime(teeTime.start_time);
  const timeLabel = formatHourMin(hour, min);

  const [bookedByMemberId, setBookedByMemberId] = useState(null);
  const [players, setPlayers] = useState([
    { type: 'member', memberNumber: '', memberName: '', guestName: '', needsCart: false, holes: null, memberId: null },
  ]);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [warnings, setWarnings] = useState([]);

  const courseHoles = selectedCourse?.holes || 18;

  const addPlayer = useCallback(() => {
    setPlayers((prev) => [
      ...prev,
      { type: 'member', memberNumber: '', memberName: '', guestName: '', needsCart: false, holes: null, memberId: null },
    ]);
  }, [courseHoles]);

  const removePlayer = useCallback((idx) => {
    setPlayers((prev) => {
      const removedPlayer = prev[idx];
      const newList = prev.filter((_, i) => i !== idx);
      if (removedPlayer?.memberId === bookedByMemberId) {
        const nextMember = newList.find((p) => p.type === 'member' && p.memberId);
        setBookedByMemberId(nextMember?.memberId ?? null);
      }
      return newList;
    });
  }, [bookedByMemberId]);

  const updatePlayer = useCallback((idx, patch) => {
    setPlayers((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  }, []);

  const handleMemberSelect = useCallback((idx, member) => {
    if (!member) {
      const deselectedMemberId = players[idx]?.memberId;
      updatePlayer(idx, { memberNumber: '', memberName: '', memberId: null });
      if (deselectedMemberId && deselectedMemberId === bookedByMemberId) {
        const nextMember = players.find((p, i) => i !== idx && p.type === 'member' && p.memberId);
        setBookedByMemberId(nextMember?.memberId ?? null);
      }
      return;
    }
    updatePlayer(idx, { memberNumber: member.member_number, memberName: member.member_name, memberId: member.id });
    if (bookedByMemberId === null) setBookedByMemberId(member.id);
  }, [updatePlayer, players, bookedByMemberId]);

  const handleSave = useCallback(async () => {
    setApiError(null);
    setWarnings([]);

    // Validate: at least one member player
    const hasMember = players.some(
      (p) => p.type === 'member' && p.memberNumber
    );
    if (!hasMember) {
      setApiError('At least one player must be a member with a valid member number.');
      return;
    }

    // Validate: holes must be selected for every player
    const missingHoles = players.some((p) => p.holes === null);
    if (missingHoles) {
      setApiError('Please select the number of holes for every player.');
      return;
    }

    const payload = {
      tee_time_id: teeTime.id,
      ...(bookedByMemberId !== null ? { booked_by_member_id: bookedByMemberId } : {}),
      players: players.map((p) => {
        if (p.type === 'member') {
          return {
            member_number: p.memberNumber,
            needs_golf_cart: p.needsCart,
            holes_to_play: p.holes,
          };
        }
        return {
          guest_name: p.guestName.trim() || 'Guest',
          needs_golf_cart: p.needsCart,
          holes_to_play: p.holes,
        };
      }),
    };

    setSaving(true);
    try {
      const data = await createBooking(payload);
      const bookingNumber = data.booking?.booking_number || data.booking_number || '';
      onCreated(`Booking ${bookingNumber} created.`, data);
      const bookingWarnings = data.booking?.warnings || [];
      if (bookingWarnings.length > 0) {
        setWarnings(bookingWarnings);
        // Keep modal open so user can read the warnings; they close manually.
      } else {
        onClose();
      }
    } catch (err) {
      setApiError(err.message || 'Failed to create booking.');
    } finally {
      setSaving(false);
    }
  }, [teeTime.id, bookedByMemberId, players, onCreated, onClose]);

  return (
    <ModalShell title={`New booking — ${timeLabel}`} size="wide" onClose={onClose}>
      <div className={styles.modalScrollBody}>
        {/* Inline API error */}
        {apiError && (
          <div className={styles.inlineError} role="alert">
            <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{apiError}</span>
          </div>
        )}

        {/* Players */}
        <div className={styles.playersSection}>
          <span className={styles.playersSectionLabel}>Players</span>
          {players.map((player, idx) => (
            <PlayerRow
              key={idx}
              player={player}
              idx={idx}
              onChange={updatePlayer}
              onMemberSelect={handleMemberSelect}
              onRemove={removePlayer}
              canRemove={players.length > 1}
              showStatus={false}
              courseHoles={courseHoles}
            />
          ))}

          <button
            type="button"
            className={styles.addPlayerBtn}
            onClick={addPlayer}
          >
            + Add player
          </button>
        </div>

        {/* Booked by */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="new-booking-booked-by">
            Booked by
          </label>
          <select
            id="new-booking-booked-by"
            className={styles.fieldSelect}
            value={bookedByMemberId ?? ''}
            onChange={(e) => setBookedByMemberId(e.target.value ? Number(e.target.value) : null)}
            disabled={saving}
          >
            {players.filter((p) => p.type === 'member' && p.memberId).length === 0 ? (
              <option value="">— add a member player first —</option>
            ) : (
              players
                .filter((p) => p.type === 'member' && p.memberId)
                .map((p) => (
                  <option key={p.memberId} value={p.memberId}>{p.memberName}</option>
                ))
            )}
          </select>
        </div>

        {/* Post-save warnings */}
        {warnings.length > 0 && (
          <div className={styles.playersSection}>
            {warnings.map((w, i) => (
              <div key={i} className={styles.inlineWarning} role="alert">
                <AlertTriangle size={14} strokeWidth={1.5} className={styles.inlineWarningIcon} />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.modalFooter}>
        {warnings.length === 0 && (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// BookingDetailModal
// ---------------------------------------------------------------------------

function BookingDetailModal({ booking, nineHolePace, onClose, onArchived, onUpdated, addToast, user }) {
  const [mode, setMode] = useState('read'); // 'read' | 'edit'

  // Local copy of booking that updates after recheck/approve without closing the modal
  const [localBooking, setLocalBooking] = useState(booking);

  // Read mode: display-only player list with status toggles
  const [players, setPlayers] = useState(booking.players || []);
  const [archiving, setArchiving] = useState(false);

  // Edit mode: form state pre-filled from booking
  const [bookedByMemberId, setBookedByMemberId] = useState(booking.booked_by_member_id || null);
  const [formPlayers, setFormPlayers] = useState(() =>
    (booking.players || []).map((p) => ({
      type: p.member_number ? 'member' : 'guest',
      memberNumber: p.member_number || '',
      memberName: p.member_name || '',
      guestName: p.guest_name || '',
      needsCart: p.needs_golf_cart || false,
      holes: p.holes_to_play || null,
      status: p.status || 'Booked',
      memberId: p.member_id || null,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const tt = localBooking.tee_time || {};
  const { hour, min } = tt.start_time ? parseISOTime(tt.start_time) : { hour: 0, min: 0 };
  const timeLabel = tt.start_time ? formatHourMin(hour, min) : '—';

  // ── Read mode ──────────────────────────────────────────────────────────────

  const handleStatusToggle = useCallback(async (idx, action) => {
    const newStatus = players[idx].status === action ? 'Booked' : action;
    const updatedPlayers = players.map((p, i) =>
      i === idx ? { ...p, status: newStatus } : p
    );
    setPlayers(updatedPlayers);
    try {
      await updateBooking(localBooking.id, { players: updatedPlayers.map((p) => ({
        id: p.id,
        ...(p.member_number ? { member_number: p.member_number } : { guest_name: p.guest_name || p.member_name || 'Guest' }),
        needs_golf_cart: p.needs_golf_cart,
        holes_to_play: p.holes_to_play,
        status: p.status,
      }))});
      onUpdated && onUpdated();
    } catch (err) {
      setPlayers(players);
      addToast(err.message || 'Failed to update player status.', 'error');
    }
  }, [players, localBooking.id, addToast, onUpdated]);

  const handleArchive = useCallback(async () => {
    setArchiving(true);
    try {
      await archiveBooking(localBooking.id);
      onArchived('Booking archived.');
      onClose();
    } catch (err) {
      addToast(err.message || 'Failed to archive booking.', 'error');
      setArchiving(false);
    }
  }, [localBooking.id, onArchived, onClose, addToast]);

  const handleRecheck = useCallback(async () => {
    setRecheckLoading(true);
    try {
      const data = await recheckBooking(localBooking.id);
      setLocalBooking(data.booking);
      addToast('Booking rechecked.');
      onUpdated && onUpdated();
    } catch (err) {
      addToast(err.message || 'Recheck failed.', 'error');
    } finally {
      setRecheckLoading(false);
    }
  }, [localBooking.id, addToast, onUpdated]);

  const handleApprove = useCallback(async () => {
    setApproveLoading(true);
    try {
      const data = await approveBooking(localBooking.id);
      setLocalBooking(data.booking);
      addToast('Booking approved.');
      onUpdated && onUpdated();
    } catch (err) {
      addToast(err.message || 'Approval failed.', 'error');
    } finally {
      setApproveLoading(false);
    }
  }, [localBooking.id, addToast, onUpdated]);

  const handleCancelBooking = useCallback(async () => {
    if (!window.confirm('Are you sure you want to cancel this booking? This cannot be undone.')) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelBooking(localBooking.id);
      onArchived('Booking cancelled.');
      onClose();
    } catch (err) {
      setCancelError(err.message || 'Failed to cancel booking.');
      setCancelling(false);
    }
  }, [localBooking.id, onArchived, onClose]);

  // ── Edit mode ──────────────────────────────────────────────────────────────

  const addFormPlayer = useCallback(() => {
    setFormPlayers((prev) => [
      ...prev,
      { type: 'member', memberNumber: '', memberName: '', guestName: '', needsCart: false, holes: null, status: 'Booked', memberId: null },
    ]);
  }, []);

  const removeFormPlayer = useCallback((idx) => {
    setFormPlayers((prev) => {
      const removedPlayer = prev[idx];
      const newList = prev.filter((_, i) => i !== idx);
      if (removedPlayer?.memberId === bookedByMemberId) {
        const nextMember = newList.find((p) => p.type === 'member' && p.memberId);
        setBookedByMemberId(nextMember?.memberId ?? null);
      }
      return newList;
    });
  }, [bookedByMemberId]);

  const updateFormPlayer = useCallback((idx, patch) => {
    setFormPlayers((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  }, []);

  const handleMemberSelect = useCallback((idx, member) => {
    if (!member) {
      const deselectedMemberId = formPlayers[idx]?.memberId;
      updateFormPlayer(idx, { memberNumber: '', memberName: '', memberId: null });
      if (deselectedMemberId && deselectedMemberId === bookedByMemberId) {
        const nextMember = formPlayers.find((p, i) => i !== idx && p.type === 'member' && p.memberId);
        setBookedByMemberId(nextMember?.memberId ?? null);
      }
      return;
    }
    updateFormPlayer(idx, { memberNumber: member.member_number, memberName: member.member_name, memberId: member.id });
    if (bookedByMemberId === null) setBookedByMemberId(member.id);
  }, [updateFormPlayer, formPlayers, bookedByMemberId]);

  const handleSave = useCallback(async () => {
    setEditError(null);
    const hasMember = formPlayers.some((p) => p.type === 'member' && p.memberNumber);
    if (!hasMember) {
      setEditError('At least one player must be a member with a valid member number.');
      return;
    }
    const missingHoles = formPlayers.some((p) => p.holes === null);
    if (missingHoles) {
      setEditError('Please select the number of holes for every player.');
      return;
    }
    const payload = {
      ...(bookedByMemberId !== null ? { booked_by_member_id: bookedByMemberId } : {}),
      players: formPlayers.map((p) =>
        p.type === 'member'
          ? { member_number: p.memberNumber, needs_golf_cart: p.needsCart, holes_to_play: p.holes, status: p.status }
          : { guest_name: p.guestName.trim() || 'Guest', needs_golf_cart: p.needsCart, holes_to_play: p.holes, status: p.status }
      ),
    };
    setSaving(true);
    try {
      await updateBooking(localBooking.id, payload);
      addToast(`Booking ${localBooking.booking_number} updated.`);
      onUpdated && onUpdated();
      onClose();
    } catch (err) {
      setEditError(err.message || 'Failed to update booking.');
    } finally {
      setSaving(false);
    }
  }, [localBooking.id, localBooking.booking_number, bookedByMemberId, formPlayers, addToast, onUpdated, onClose]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const bookingInfoPanel = (
    <BookingInfoPanel
      teeTimeLabel={`${timeLabel}${tt.golf_course_name ? ` · ${tt.golf_course_name}` : ''}${tt.hole != null ? ` · Hole ${tt.hole}` : ''}`}
      isProblematic={localBooking.is_problematic}
      isValidated={localBooking.is_validated !== false}
      warnings={localBooking.warnings || []}
      warningsLastChecked={localBooking.warnings_last_checked_at || null}
      onRecheck={handleRecheck}
      onApprove={isSupervisorOrAdmin(user) ? handleApprove : null}
      recheckLoading={recheckLoading}
      approveLoading={approveLoading}
      disabled={false}
    />
  );

  if (mode === 'edit') {
    return (
      <ModalShell title={`Edit booking — ${localBooking.booking_number}`} size="wide" onClose={onClose}>
        <div className={styles.modalScrollBody}>
          {bookingInfoPanel}
          {editError && (
            <div className={styles.inlineError} role="alert">
              <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{editError}</span>
            </div>
          )}
          {/* Players */}
          <div className={styles.playersSection}>
            <span className={styles.playersSectionLabel}>Players</span>
            {formPlayers.map((player, idx) => (
              <PlayerRow
                key={idx}
                player={player}
                idx={idx}
                onChange={updateFormPlayer}
                onMemberSelect={handleMemberSelect}
                onRemove={removeFormPlayer}
                canRemove={formPlayers.length > 1}
                showStatus={true}
              />
            ))}
            <button type="button" className={styles.addPlayerBtn} onClick={addFormPlayer}>
              + Add player
            </button>
          </div>
          {/* Booked by */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="edit-booking-booked-by">Booked by</label>
            <select
              id="edit-booking-booked-by"
              className={styles.fieldSelect}
              value={bookedByMemberId ?? ''}
              onChange={(e) => setBookedByMemberId(e.target.value ? Number(e.target.value) : null)}
              disabled={saving}
            >
              {formPlayers.filter((p) => p.type === 'member' && p.memberId).length === 0 ? (
                <option value="">— add a member player first —</option>
              ) : (
                formPlayers
                  .filter((p) => p.type === 'member' && p.memberId)
                  .map((p) => (
                    <option key={p.memberId} value={p.memberId}>{p.memberName}</option>
                  ))
              )}
            </select>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <div className={styles.modalFooterLeft}>
            <ArchiveButton
              confirmMessage={`Archive booking ${localBooking.booking_number}? This will remove it from active use.`}
              onConfirm={handleArchive}
              loading={archiving}
            />
          </div>
          <button type="button" className={styles.btnSecondary} onClick={() => { setMode('read'); setEditError(null); }}>
            Cancel
          </button>
          <button type="button" className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title={`Booking — ${localBooking.booking_number}`} size="wide" onClose={onClose}>
      <div className={styles.modalScrollBody}>
        {bookingInfoPanel}
        <div className={styles.bookingDetailRow}>
          <span className={styles.bookingDetailRowLabel}>Booked by</span>
          <span>{localBooking.booked_by_name || '—'}</span>
        </div>
        {/* Players table */}
        {players.length > 0 && (
          <table className={styles.playersTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Member #</th>
                <th>Cart</th>
                <th>Holes</th>
                {(tt.hole === 1 || tt.hole === 10) && <th>Est. Arrival</th>}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player, idx) => {
                const { hour, min } = parseISOTime(tt.start_time || '');
                const estArrival = computeEstArrival(
                  player.holes_to_play ?? player.holes ?? null,
                  tt.hole,
                  hour * 60 + min,
                  nineHolePace,
                );
                return (
                <tr key={player.id || idx}>
                  <td className={styles.playerNameCell}>
                    {player.member_name || player.guest_name || '—'}
                  </td>
                  <td>
                    {player.member_number ? (
                      <span className={styles.playerMemberNumber}>{player.member_number}</span>
                    ) : (
                      <span style={{ color: 'var(--cp-gray-400)', fontSize: 14 }}>Guest</span>
                    )}
                  </td>
                  <td>{player.needs_golf_cart ? 'Yes' : 'No'}</td>
                  <td>{player.holes_to_play}</td>
                  {(tt.hole === 1 || tt.hole === 10) && (
                    <td style={{ color: 'var(--cp-gray-400)', fontSize: 13 }}>
                      {estArrival ? `Hole ${estArrival.destHole} · ${estArrival.time}` : ''}
                    </td>
                  )}
                  <td>
                    <div className={styles.statusBtnGroup}>
                      <button
                        type="button"
                        className={`${styles.statusBtn} ${player.status === 'Paid' ? styles.statusBtnPaidActive : ''}`}
                        onClick={() => handleStatusToggle(idx, 'Paid')}
                        aria-pressed={player.status === 'Paid'}
                      >
                        Paid
                      </button>
                      <button
                        type="button"
                        className={`${styles.statusBtn} ${player.status === 'No Show' ? styles.statusBtnNoShowActive : ''}`}
                        onClick={() => handleStatusToggle(idx, 'No Show')}
                        aria-pressed={player.status === 'No Show'}
                      >
                        No show
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        )}
      </div>
      {cancelError && (
        <div className={styles.inlineError} role="alert">
          <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{cancelError}</span>
        </div>
      )}
      <div className={styles.modalFooter}>
        <div className={styles.modalFooterLeft}>
          <ArchiveButton
            confirmMessage={`Archive booking ${localBooking.booking_number}? This will remove it from active use.`}
            onConfirm={handleArchive}
            loading={archiving}
          />
          <button
            type="button"
            className={styles.btnCancelBooking}
            onClick={handleCancelBooking}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling…' : 'Cancel Booking'}
          </button>
        </div>
        <button type="button" className={styles.btnPrimary} onClick={() => setMode('edit')}>
          Edit
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// TeeTimeModal (edit tee time — accessed via pencil icon)
// ---------------------------------------------------------------------------

function TeeTimeModal({ teeTime, gapMins, teeTimes, bookings, onClose, onSaved }) {
  const [datetimeValue, setDatetimeValue] = useState(teeTime.start_time.slice(0, 16));
  const [saving, setSaving] = useState(false);



  const gapWarning = useMemo(() => {
    const timePart = datetimeValue.slice(11, 16);
    const [h, m] = timePart.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return false;
    const newMins = h * 60 + m;
    for (const tt of teeTimes) {
      if (tt.id === teeTime.id) continue;
      const other = parseISOTime(tt.start_time);
      const otherMins = other.hour * 60 + other.min;
      if (Math.abs(otherMins - newMins) < gapMins) return true;
    }
    return false;
  }, [datetimeValue, teeTimes, teeTime.id, gapMins]);

  const handleSave = useCallback(async () => {
    const timePart = datetimeValue.slice(11, 16);
    const [h, m] = timePart.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    setSaving(true);
    try {
      const iso = datetimeValue + ':00';
      await updateTeeTime(teeTime.id, { start_time: iso });
      onSaved(`Tee time moved to ${formatHourMin(h, m)}.`);
      onClose();
    } catch (err) {
      const msg = err.message || 'Failed to update tee time.';
      if (msg.toLowerCase().includes('already exists')) {
        onSaved('Another tee time already exists at that time.');
      } else {
        onSaved(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [datetimeValue, teeTime.id, onSaved, onClose]);

  const { hour: dispH, min: dispMin } = parseISOTime(teeTime.start_time);

  return (
    <ModalShell title={`Tee time — ${formatHourMin(dispH, dispMin)}`} size="normal" onClose={onClose}>
      <div className={styles.modalBody}>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="tt-time-input">
            Date & time
          </label>
          <input
            id="tt-time-input"
            type="datetime-local"
            className={styles.fieldInput}
            value={datetimeValue}
            onChange={(e) => setDatetimeValue(e.target.value)}
          />
        </div>
        {gapWarning && (
          <div className={styles.inlineWarning} role="alert">
            <AlertTriangle
              size={14}
              strokeWidth={1.5}
              className={styles.inlineWarningIcon}
            />
            <span>
              This time violates the {gapMins}-minute gap setting. The
              calendar will switch to compressed view.
            </span>
          </div>
        )}

      </div>
      <div className={styles.modalFooter}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// TeeTimeDetailModal — read/edit view opened by clicking a tee tile
// ---------------------------------------------------------------------------

function TeeTimeDetailModal({ teeTime, gapMins, teeTimes, selectedCourse, onClose, onSaved, onNewBooking, addToast }) {
  const { hour: dispH, min: dispMin } = parseISOTime(teeTime.start_time);
  const [mode, setMode] = useState('read'); // 'read' | 'edit'
  const [datetimeValue, setDatetimeValue] = useState(teeTime.start_time.slice(0, 16));
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const gapWarning = useMemo(() => {
    const timePart = datetimeValue.slice(11, 16);
    const [h, m] = timePart.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return false;
    const newMins = h * 60 + m;
    return teeTimes.some((tt) => {
      if (tt.id === teeTime.id) return false;
      const other = parseISOTime(tt.start_time);
      return Math.abs(other.hour * 60 + other.min - newMins) < gapMins;
    });
  }, [datetimeValue, teeTimes, teeTime.id, gapMins]);

  const handleSave = useCallback(async () => {
    const timePart = datetimeValue.slice(11, 16);
    const [h, m] = timePart.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    setSaving(true);
    try {
      await updateTeeTime(teeTime.id, { start_time: datetimeValue + ':00' });
      const { hour: nh, min: nm } = parseISOTime(datetimeValue + ':00');
      onSaved(`Tee time moved to ${formatHourMin(nh, nm)}.`);
      onClose();
    } catch (err) {
      setEditError(err.message || 'Failed to update tee time.');
    } finally {
      setSaving(false);
    }
  }, [datetimeValue, teeTime.id, onSaved, onClose]);

  const handleArchive = useCallback(async () => {
    setArchiving(true);
    try {
      await updateTeeTime(teeTime.id, { is_archived: true });
      onSaved('Tee time archived.');
      onClose();
    } catch (err) {
      addToast(err.message || 'Failed to archive tee time.', 'error');
      setArchiving(false);
    }
  }, [teeTime.id, onSaved, onClose, addToast]);

  return (
    <ModalShell
      title={mode === 'read' ? `Tee time — ${formatHourMin(dispH, dispMin)}` : 'Edit tee time'}
      size="normal"
      onClose={onClose}
    >
      {mode === 'read' ? (
        <>
          <div className={styles.modalBody}>
            <div className={styles.bookingDetailInfo}>
              <div className={styles.bookingDetailRow}>
                <span className={styles.bookingDetailRowLabel}>Start time</span>
                <span>{formatHourMin(dispH, dispMin)}</span>
              </div>
              <div className={styles.bookingDetailRow}>
                <span className={styles.bookingDetailRowLabel}>Course</span>
                <span>{teeTime.golf_course_name || selectedCourse?.name || '—'}</span>
              </div>
              <div className={styles.bookingDetailRow}>
                <span className={styles.bookingDetailRowLabel}>Hole</span>
                {teeTime.hole != null
                  ? <HolePill hole={teeTime.hole} />
                  : <span>—</span>
                }
              </div>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <div className={styles.modalFooterLeft}>
              <ArchiveButton
                confirmMessage="Archive this tee time? It will be removed from the calendar."
                onConfirm={handleArchive}
                loading={archiving}
              />
            </div>
            <button type="button" className={styles.btnSecondary} onClick={onNewBooking}>
              + New booking
            </button>
            <button type="button" className={styles.btnPrimary} onClick={() => setMode('edit')}>
              Edit
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.modalBody}>
            {editError && (
              <div className={styles.inlineError} role="alert">
                <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{editError}</span>
              </div>
            )}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="tt-detail-datetime">Date & time</label>
              <input
                id="tt-detail-datetime"
                type="datetime-local"
                className={styles.fieldInput}
                value={datetimeValue}
                onChange={(e) => { setDatetimeValue(e.target.value); setEditError(null); }}
                autoFocus
              />
            </div>
            {gapWarning && (
              <div className={styles.inlineWarning} role="alert">
                <AlertTriangle size={14} strokeWidth={1.5} className={styles.inlineWarningIcon} />
                <span>This time violates the {gapMins}-minute gap setting. The calendar will switch to compressed view.</span>
              </div>
            )}
          </div>
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => { setMode('read'); setEditError(null); }}
            >
              Cancel
            </button>
            <button type="button" className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// New tee time creation modal
// ---------------------------------------------------------------------------

function NewTeeTimeModal({ gapMins, teeTimes, currentDate, selectedCourseId, selectedHole, onClose, onCreated }) {
  const [timeValue, setTimeValue] = useState('');
  const [saving, setSaving] = useState(false);

  const gapWarning = useMemo(() => {
    if (!timeValue) return false;
    const [h, m] = timeValue.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return false;
    const newMins = h * 60 + m;
    for (const tt of teeTimes) {
      const other = parseISOTime(tt.start_time);
      const otherMins = other.hour * 60 + other.min;
      if (Math.abs(otherMins - newMins) < gapMins) return true;
    }
    return false;
  }, [timeValue, teeTimes, gapMins]);

  const duplicateWarning = useMemo(() => {
    if (!timeValue) return false;
    const [h, m] = timeValue.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return false;
    for (const tt of teeTimes) {
      const other = parseISOTime(tt.start_time);
      if (other.hour === h && other.min === m) return true;
    }
    return false;
  }, [timeValue, teeTimes]);

  const handleSave = useCallback(async () => {
    if (!timeValue) return;
    const [h, m] = timeValue.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    setSaving(true);
    try {
      const iso = buildISODatetime(currentDate, h, m);
      await createTeeTime({
        start_time: iso,
        golf_course_id: selectedCourseId,
        hole: selectedHole,
      });
      onCreated(`Tee time created at ${formatHourMin(h, m)}.`);
      onClose();
    } catch (err) {
      onCreated(err.message || 'Failed to create tee time.');
    } finally {
      setSaving(false);
    }
  }, [timeValue, currentDate, selectedCourseId, selectedHole, onCreated, onClose]);

  return (
    <ModalShell title="New tee time" size="normal" onClose={onClose}>
      <div className={styles.modalBody}>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="new-tt-time-input">
            Start time
          </label>
          <input
            id="new-tt-time-input"
            type="time"
            className={styles.fieldInput}
            value={timeValue}
            onChange={(e) => setTimeValue(e.target.value)}
            autoFocus
          />
        </div>
        {gapWarning && !duplicateWarning && (
          <div className={styles.inlineWarning} role="alert">
            <AlertTriangle
              size={14}
              strokeWidth={1.5}
              className={styles.inlineWarningIcon}
            />
            <span>
              This time violates the {gapMins}-minute gap setting.
            </span>
          </div>
        )}
        {duplicateWarning && (
          <div className={styles.inlineWarning} role="alert">
            <AlertTriangle
              size={14}
              strokeWidth={1.5}
              className={styles.inlineWarningIcon}
            />
            <span>A tee time already exists at this time.</span>
          </div>
        )}
      </div>
      <div className={styles.modalFooter}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={handleSave}
          disabled={saving || !timeValue}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// BookingConflictModal
// ---------------------------------------------------------------------------

function BookingConflictModal({ affectedBookings, onConfirm, onCancel, confirming }) {
  return (
    <ModalShell title="Affected bookings" size="normal" onClose={onCancel} zIndex={200}>
      <div className={styles.modalBody}>
        <p style={{ fontSize: 13, color: 'var(--cp-gray-700)', margin: 0, lineHeight: 1.5 }}>
          This action will affect the following{' '}
          {affectedBookings.length === 1 ? 'booking' : 'bookings'}. Archiving
          {affectedBookings.length === 1 ? ' it' : ' them'} cannot be undone.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {affectedBookings.map((bn) => (
            <span
              key={bn}
              style={{
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: 12,
                background: '#FEF3CD',
                border: '1px solid var(--cp-warning)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px 8px',
                color: '#7A5800',
              }}
            >
              {bn}
            </span>
          ))}
        </div>
      </div>
      <div className={styles.modalFooter}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.btnDanger}
          onClick={onConfirm}
          disabled={confirming}
        >
          {confirming ? 'Archiving…' : 'Archive and proceed'}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// BlockDetailModal
// ---------------------------------------------------------------------------

function BlockDetailModal({ block, blockTypes, onClose, onArchived, onSaved, addToast }) {
  const [mode, setMode] = useState('read'); // 'read' | 'edit'

  // Edit form state
  const [editName, setEditName] = useState(block.name || '');
  const [editBlockTypeId, setEditBlockTypeId] = useState(block.block_type_id || '');
  const [editStartTime, setEditStartTime] = useState(block.start_time ? block.start_time.slice(0, 16) : '');
  const [editEndTime, setEditEndTime] = useState(block.end_time ? block.end_time.slice(0, 16) : '');
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [conflictBookings, setConflictBookings] = useState(null); // null | string[]
  const [conflictConfirming, setConflictConfirming] = useState(false);

  const { hour: startH, min: startMin } = parseISOTime(block.start_time || '');
  const { hour: endH, min: endMin } = parseISOTime(block.end_time || '');

  const handleArchive = useCallback(async () => {
    setArchiving(true);
    try {
      await archiveBlock(block.id);
      onArchived('Block archived.');
      onClose();
    } catch (err) {
      addToast(err.message || 'Failed to archive block.', 'error');
      setArchiving(false);
    }
  }, [block.id, onArchived, onClose, addToast]);

  const attemptSave = useCallback(async (forceArchiveIds) => {
    setEditError(null);
    setSaving(true);
    try {
      const payload = {
        name: editName.trim(),
        block_type_id: editBlockTypeId ? Number(editBlockTypeId) : undefined,
        start_time: editStartTime ? editStartTime + ':00' : undefined,
        end_time: editEndTime ? editEndTime + ':00' : undefined,
      };
      if (forceArchiveIds && forceArchiveIds.length > 0) {
        payload.archive_booking_ids = forceArchiveIds;
      }
      await updateBlock(block.id, payload);
      onSaved('Block updated.');
      onClose();
    } catch (err) {
      const msg = err.message || '';
      // Try to parse affected_bookings from the error message
      let affected = null;
      try {
        const parsed = JSON.parse(msg);
        if (parsed.affected_bookings) affected = parsed.affected_bookings;
      } catch (_) {
        // Backend may embed JSON in the error string
        const match = msg.match(/affected_bookings["\s:]+\[([^\]]+)\]/);
        if (match) {
          affected = match[1].split(',').map((s) => s.trim().replace(/"/g, ''));
        }
      }
      if (affected && affected.length > 0) {
        setConflictBookings(affected);
      } else {
        setEditError(msg || 'Failed to update block.');
      }
    } finally {
      setSaving(false);
    }
  }, [block.id, editName, editBlockTypeId, editStartTime, editEndTime, onSaved, onClose]);

  const handleSave = useCallback(() => {
    attemptSave(null);
  }, [attemptSave]);

  const handleConflictConfirm = useCallback(async () => {
    if (!conflictBookings) return;
    setConflictConfirming(true);
    try {
      await Promise.all(conflictBookings.map((bn) => {
        // conflictBookings are booking numbers; we need to archive by the booking number
        // The backend should accept archive_booking_numbers on the retry
        return Promise.resolve(); // actual archive is done via payload flag
      }));
      await attemptSave(conflictBookings);
    } finally {
      setConflictConfirming(false);
      setConflictBookings(null);
    }
  }, [conflictBookings, attemptSave]);

  return (
    <ModalShell
      title={mode === 'read' ? `Block — ${block.name}` : 'Edit block'}
      size="normal"
      onClose={() => { if (!conflictBookings) onClose(); }}
    >
      {mode === 'read' ? (
        <>
          <div className={styles.modalBody}>
            <div className={styles.bookingDetailInfo}>
              <div className={styles.bookingDetailRow}>
                <span className={styles.bookingDetailRowLabel}>Name</span>
                <span>{block.name}</span>
              </div>
              <div className={styles.bookingDetailRow}>
                <span className={styles.bookingDetailRowLabel}>Type</span>
                <span>{block.block_type_name || '—'}</span>
              </div>
              <div className={styles.bookingDetailRow}>
                <span className={styles.bookingDetailRowLabel}>Course</span>
                <span>{block.golf_course_name || '—'}</span>
              </div>
              <div className={styles.bookingDetailRow}>
                <span className={styles.bookingDetailRowLabel}>Start</span>
                <span>{formatHourMin(startH, startMin)}</span>
              </div>
              <div className={styles.bookingDetailRow}>
                <span className={styles.bookingDetailRowLabel}>End</span>
                <span>{formatHourMin(endH, endMin)}</span>
              </div>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <div className={styles.modalFooterLeft}>
              <ArchiveButton
                confirmMessage={`Archive block "${block.name}"? This will remove it from active use.`}
                onConfirm={handleArchive}
                loading={archiving}
              />
            </div>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => setMode('edit')}
            >
              Edit
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.modalBody}>
            {editError && (
              <div className={styles.inlineError} role="alert">
                <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{editError}</span>
              </div>
            )}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="block-edit-name">
                Name
              </label>
              <input
                id="block-edit-name"
                type="text"
                className={styles.fieldInput}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoFocus
              />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="block-edit-type">
                Block type
              </label>
              <select
                id="block-edit-type"
                className={`${styles.fieldInput} ${styles.select}`}
                value={editBlockTypeId}
                onChange={(e) => setEditBlockTypeId(e.target.value)}
              >
                <option value="" disabled>Select a type…</option>
                {blockTypes.map((bt) => (
                  <option key={bt.id} value={bt.id}>{bt.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="block-edit-start">
                Start time
              </label>
              <input
                id="block-edit-start"
                type="datetime-local"
                className={styles.fieldInput}
                value={editStartTime}
                onChange={(e) => setEditStartTime(e.target.value)}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="block-edit-end">
                End time
              </label>
              <input
                id="block-edit-end"
                type="datetime-local"
                className={styles.fieldInput}
                value={editEndTime}
                onChange={(e) => setEditEndTime(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => { setMode('read'); setEditError(null); setConflictBookings(null); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleSave}
              disabled={saving || !editName.trim()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
      {conflictBookings && (
        <BookingConflictModal
          affectedBookings={conflictBookings}
          onConfirm={handleConflictConfirm}
          onCancel={() => setConflictBookings(null)}
          confirming={conflictConfirming}
        />
      )}
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// CreateBlockModal
// ---------------------------------------------------------------------------

function CreateBlockModal({ blockTypes, selectedCourseId, currentDate, prefilledStartTime, onClose, onCreated, addToast }) {
  const defaultStart = prefilledStartTime || (currentDate ? `${currentDate}T00:00` : '');
  const defaultEnd = useMemo(() => {
    const base = prefilledStartTime || (currentDate ? `${currentDate}T00:00` : '');
    if (!base) return '';
    const [datePart, timePart] = base.split('T');
    if (!timePart) return '';
    const [hStr, mStr] = timePart.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (isNaN(h) || isNaN(m)) return '';
    const totalMins = h * 60 + m + 120;
    const newH = Math.min(Math.floor(totalMins / 60), 23);
    const newM = totalMins % 60;
    return `${datePart}T${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
  }, [prefilledStartTime, currentDate]);

  const [name, setName] = useState('');
  const [blockTypeId, setBlockTypeId] = useState(blockTypes.length > 0 ? String(blockTypes[0].id) : '');
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Sync blockTypeId when blockTypes loads
  useEffect(() => {
    if (blockTypes.length > 0 && !blockTypeId) {
      setBlockTypeId(String(blockTypes[0].id));
    }
  }, [blockTypes, blockTypeId]);

  const handleSave = useCallback(async () => {
    setApiError(null);
    if (!name.trim()) {
      setApiError('Name is required.');
      return;
    }
    if (!blockTypeId) {
      setApiError('Please select a block type.');
      return;
    }
    if (!startTime || !endTime) {
      setApiError('Start and end time are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        block_type_id: Number(blockTypeId),
        golf_course_id: selectedCourseId,
        start_time: startTime + ':00',
        end_time: endTime + ':00',
      };
      await createBlock(payload);
      onCreated('Block created.');
      onClose();
    } catch (err) {
      const msg = err.message || 'Failed to create block.';
      // Check for affected_bookings conflict
      let affected = null;
      try {
        const parsed = JSON.parse(msg);
        if (parsed.affected_bookings) affected = parsed.affected_bookings;
      } catch (_) {
        const match = msg.match(/affected_bookings["\s:]+\[([^\]]+)\]/);
        if (match) {
          affected = match[1].split(',').map((s) => s.trim().replace(/"/g, ''));
        }
      }
      if (affected && affected.length > 0) {
        setApiError(
          `Cannot create block — ${affected.length} active booking${affected.length !== 1 ? 's' : ''} fall in this range: ${affected.join(', ')}. Please reschedule or archive them first.`
        );
      } else {
        setApiError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [name, blockTypeId, selectedCourseId, startTime, endTime, onCreated, onClose]);

  return (
    <ModalShell title="New block" size="normal" onClose={onClose}>
      <div className={styles.modalBody}>
        {apiError && (
          <div className={styles.inlineError} role="alert">
            <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{apiError}</span>
          </div>
        )}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="new-block-name">
            Name
          </label>
          <input
            id="new-block-name"
            type="text"
            className={styles.fieldInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Morning maintenance"
            autoFocus
          />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="new-block-type">
            Block type
          </label>
          <select
            id="new-block-type"
            className={`${styles.fieldInput} ${styles.select}`}
            value={blockTypeId}
            onChange={(e) => setBlockTypeId(e.target.value)}
          >
            {blockTypes.length === 0 ? (
              <option value="" disabled>Loading…</option>
            ) : (
              <>
                <option value="" disabled>Select a type…</option>
                {blockTypes.map((bt) => (
                  <option key={bt.id} value={bt.id}>{bt.name}</option>
                ))}
              </>
            )}
          </select>
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="new-block-start">
            Start time
          </label>
          <input
            id="new-block-start"
            type="datetime-local"
            className={styles.fieldInput}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="new-block-end">
            End time
          </label>
          <input
            id="new-block-end"
            type="datetime-local"
            className={styles.fieldInput}
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>
      </div>
      <div className={styles.modalFooter}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={handleSave}
          disabled={saving || !name.trim() || !!apiError}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// UnifiedCreateModal
// ---------------------------------------------------------------------------

function UnifiedCreateModal({
  slotHour,
  slotMinute,
  existingTeeTime,
  teeTimes,
  gapMins,
  blockTypes,
  selectedCourseId,
  selectedHole,
  selectedCourse,
  currentDate,
  onClose,
  onTeeTimeCreated,
  onBookingCreated,
  onBlockCreated,
  addToast,
}) {
  const timeLabel = formatHourMin(slotHour, slotMinute);
  const defaultType = existingTeeTime ? 'booking' : 'teetime';
  const [selectedType, setSelectedType] = useState(defaultType);

  // Tee time section
  const hStr = String(slotHour).padStart(2, '0');
  const mStr = String(slotMinute).padStart(2, '0');
  const [ttTimeValue, setTtTimeValue] = useState(`${hStr}:${mStr}`);

  // Booking section
  const [bookedByMemberId, setBookedByMemberId] = useState(null);
  const [players, setPlayers] = useState([
    { type: 'member', memberNumber: '', memberName: '', guestName: '', needsCart: false, holes: null, memberId: null },
  ]);
  const [bookingWarnings, setBookingWarnings] = useState([]);

  // Block section
  const slotISO = `${currentDate}T${hStr}:${mStr}`;
  const [blockName, setBlockName] = useState('');
  const [blockTypeId, setBlockTypeId] = useState(blockTypes.length > 0 ? String(blockTypes[0].id) : '');
  const [blockStartTime, setBlockStartTime] = useState(slotISO);
  const [blockEndTime, setBlockEndTime] = useState(() => {
    const [datePart, timePart] = slotISO.split('T');
    const [h, m] = timePart.split(':').map(Number);
    const totalMins = h * 60 + m + 120;
    const newH = Math.min(Math.floor(totalMins / 60), 23);
    const newM = totalMins % 60;
    return `${datePart}T${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
  });

  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Sync blockTypeId when blockTypes loads
  useEffect(() => {
    if (blockTypes.length > 0 && !blockTypeId) {
      setBlockTypeId(String(blockTypes[0].id));
    }
  }, [blockTypes, blockTypeId]);

  const courseHoles = selectedCourse?.holes || 18;

  const addPlayer = useCallback(() => {
    setPlayers((prev) => [
      ...prev,
      { type: 'member', memberNumber: '', memberName: '', guestName: '', needsCart: false, holes: null, memberId: null },
    ]);
  }, []);

  const removePlayer = useCallback((idx) => {
    setPlayers((prev) => {
      const removedPlayer = prev[idx];
      const newList = prev.filter((_, i) => i !== idx);
      if (removedPlayer?.memberId === bookedByMemberId) {
        const nextMember = newList.find((p) => p.type === 'member' && p.memberId);
        setBookedByMemberId(nextMember?.memberId ?? null);
      }
      return newList;
    });
  }, [bookedByMemberId]);

  const updatePlayer = useCallback((idx, patch) => {
    setPlayers((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  }, []);

  const handleMemberSelect = useCallback((idx, member) => {
    if (!member) {
      const deselectedMemberId = players[idx]?.memberId;
      updatePlayer(idx, { memberNumber: '', memberName: '', memberId: null });
      if (deselectedMemberId && deselectedMemberId === bookedByMemberId) {
        const nextMember = players.find((p, i) => i !== idx && p.type === 'member' && p.memberId);
        setBookedByMemberId(nextMember?.memberId ?? null);
      }
      return;
    }
    updatePlayer(idx, { memberNumber: member.member_number, memberName: member.member_name, memberId: member.id });
    if (bookedByMemberId === null) setBookedByMemberId(member.id);
  }, [updatePlayer, players, bookedByMemberId]);

  // Duplicate tee time check
  const teeTimeDuplicate = useMemo(() => {
    if (selectedType !== 'teetime' || !ttTimeValue) return false;
    const [h, m] = ttTimeValue.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return false;
    return teeTimes.some((tt) => {
      const { hour, min } = parseISOTime(tt.start_time);
      return hour === h && min === m;
    });
  }, [selectedType, ttTimeValue, teeTimes]);

  // Gap warning (not duplicate, just too close)
  const teeTimeGapWarning = useMemo(() => {
    if (selectedType !== 'teetime' || !ttTimeValue || teeTimeDuplicate) return false;
    const [h, m] = ttTimeValue.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return false;
    const newMins = h * 60 + m;
    return teeTimes.some((tt) => {
      const { hour, min } = parseISOTime(tt.start_time);
      const diff = Math.abs(hour * 60 + min - newMins);
      return diff > 0 && diff < gapMins;
    });
  }, [selectedType, ttTimeValue, teeTimes, gapMins, teeTimeDuplicate]);

  // Booking: no tee time at slot
  const bookingNoTeeTime = selectedType === 'booking' && !existingTeeTime;

  const handleSave = useCallback(async () => {
    setApiError(null);
    setSaving(true);
    try {
      if (selectedType === 'teetime') {
        if (teeTimeDuplicate) {
          setApiError('A tee time already exists at this time.');
          setSaving(false);
          return;
        }
        const [h, m] = ttTimeValue.split(':').map(Number);
        await createTeeTime({
          start_time: buildISODatetime(currentDate, h, m),
          golf_course_id: selectedCourseId,
          hole: selectedHole,
        });
        onTeeTimeCreated(`Tee time created at ${formatHourMin(h, m)}.`);
        onClose();

      } else if (selectedType === 'booking') {
        if (!existingTeeTime) {
          setApiError('No tee time exists at this slot. Create a tee time first.');
          setSaving(false);
          return;
        }
        const hasMember = players.some((p) => p.type === 'member' && p.memberNumber);
        if (!hasMember) {
          setApiError('At least one player must be a member with a valid member number.');
          setSaving(false);
          return;
        }
        const missingHoles = players.some((p) => p.holes === null);
        if (missingHoles) {
          setApiError('Please select the number of holes for every player.');
          setSaving(false);
          return;
        }
        const payload = {
          tee_time_id: existingTeeTime.id,
          ...(bookedByMemberId !== null ? { booked_by_member_id: bookedByMemberId } : {}),
          players: players.map((p) =>
            p.type === 'member'
              ? { member_number: p.memberNumber, needs_golf_cart: p.needsCart, holes_to_play: p.holes }
              : { guest_name: p.guestName.trim() || 'Guest', needs_golf_cart: p.needsCart, holes_to_play: p.holes }
          ),
        };
        const data = await createBooking(payload);
        const bn = data.booking?.booking_number || data.booking_number || '';
        onBookingCreated(`Booking ${bn} created.`, data);
        if (data.warnings && data.warnings.length > 0) {
          setBookingWarnings(data.warnings);
          // Keep modal open so user reads warnings
        } else {
          onClose();
        }

      } else {
        // block
        if (!blockName.trim()) { setApiError('Name is required.'); setSaving(false); return; }
        if (!blockTypeId) { setApiError('Please select a block type.'); setSaving(false); return; }
        if (!blockStartTime || !blockEndTime) { setApiError('Start and end time are required.'); setSaving(false); return; }
        const payload = {
          name: blockName.trim(),
          block_type_id: Number(blockTypeId),
          golf_course_id: selectedCourseId,
          start_time: blockStartTime + ':00',
          end_time: blockEndTime + ':00',
        };
        try {
          await createBlock(payload);
          onBlockCreated('Block created.');
          onClose();
        } catch (err) {
          const msg = err.message || 'Failed to create block.';
          let affected = null;
          try {
            const parsed = JSON.parse(msg);
            if (parsed.affected_bookings) affected = parsed.affected_bookings;
          } catch (_) {
            const match = msg.match(/affected_bookings["\s:]+\[([^\]]+)\]/);
            if (match) affected = match[1].split(',').map((s) => s.trim().replace(/"/g, ''));
          }
          if (affected && affected.length > 0) {
            setApiError(
              `Cannot create block — ${affected.length} active booking${affected.length !== 1 ? 's' : ''} fall in this range: ${affected.join(', ')}. Please reschedule or archive them first.`
            );
          } else {
            setApiError(msg);
          }
          setSaving(false);
          return;
        }
      }
    } catch (err) {
      setApiError(err.message || 'Failed to create.');
    } finally {
      setSaving(false);
    }
  }, [
    selectedType, ttTimeValue, teeTimeDuplicate, existingTeeTime, bookedByMemberId, players,
    blockName, blockTypeId, blockStartTime, blockEndTime, selectedCourseId, selectedHole,
    currentDate, onClose, onTeeTimeCreated, onBookingCreated, onBlockCreated,
  ]);

  return (
    <ModalShell title={`New item — ${timeLabel}`} size="wide" onClose={onClose}>
      <div className={styles.modalScrollBody}>
          {/* Type selector */}
          <div className={styles.unifiedTypeSelector}>
            {[
              { key: 'teetime', label: 'Tee time' },
              { key: 'booking', label: 'Booking' },
              { key: 'block', label: 'Block' },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`${styles.unifiedTypeBtn} ${selectedType === key ? styles.unifiedTypeBtnActive : ''}`}
                onClick={() => { setSelectedType(key); setApiError(null); setBookingWarnings([]); }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Global API error */}
          {apiError && (
            <div className={styles.inlineError} role="alert">
              <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{apiError}</span>
            </div>
          )}

          {/* Tee time form */}
          {selectedType === 'teetime' && (
            <>
              {teeTimeDuplicate && (
                <div className={styles.inlineError} role="alert">
                  <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>A tee time already exists at {formatHourMin(slotHour, slotMinute)} for this course and hole. Choose a different time.</span>
                </div>
              )}
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="unified-tt-time">Start time</label>
                <input
                  id="unified-tt-time"
                  type="time"
                  className={styles.fieldInput}
                  value={ttTimeValue}
                  onChange={(e) => { setTtTimeValue(e.target.value); setApiError(null); }}
                  autoFocus={!teeTimeDuplicate}
                />
              </div>
              {teeTimeGapWarning && (
                <div className={styles.inlineWarning} role="alert">
                  <AlertTriangle size={14} strokeWidth={1.5} className={styles.inlineWarningIcon} />
                  <span>This time violates the {gapMins}-minute gap setting.</span>
                </div>
              )}
            </>
          )}

          {/* Booking form */}
          {selectedType === 'booking' && (
            <>
              {bookingNoTeeTime ? (
                <div className={styles.inlineError} role="alert">
                  <AlertTriangle size={14} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>No tee time exists at {formatHourMin(slotHour, slotMinute)}. Create a tee time first before adding a booking.</span>
                </div>
              ) : (
                <>
                  <div className={styles.playersSection}>
                    <span className={styles.playersSectionLabel}>Players</span>
                    {players.map((player, idx) => (
                      <PlayerRow
                        key={idx}
                        player={player}
                        idx={idx}
                        onChange={updatePlayer}
                        onMemberSelect={handleMemberSelect}
                        onRemove={removePlayer}
                        canRemove={players.length > 1}
                        showStatus={false}
                        courseHoles={courseHoles}
                      />
                    ))}
                    <button type="button" className={styles.addPlayerBtn} onClick={addPlayer}>
                      + Add player
                    </button>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="unified-booked-by">Booked by</label>
                    <select
                      id="unified-booked-by"
                      className={styles.fieldSelect}
                      value={bookedByMemberId ?? ''}
                      onChange={(e) => setBookedByMemberId(e.target.value ? Number(e.target.value) : null)}
                      disabled={saving}
                    >
                      {players.filter((p) => p.type === 'member' && p.memberId).length === 0 ? (
                        <option value="">— add a member player first —</option>
                      ) : (
                        players
                          .filter((p) => p.type === 'member' && p.memberId)
                          .map((p) => (
                            <option key={p.memberId} value={p.memberId}>{p.memberName}</option>
                          ))
                      )}
                    </select>
                  </div>
                  {bookingWarnings.length > 0 && (
                    <div className={styles.playersSection}>
                      {bookingWarnings.map((w, i) => (
                        <div key={i} className={styles.inlineWarning} role="alert">
                          <AlertTriangle size={14} strokeWidth={1.5} className={styles.inlineWarningIcon} />
                          <span>{w}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Block form */}
          {selectedType === 'block' && (
            <>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="unified-block-name">Name</label>
                <input
                  id="unified-block-name"
                  type="text"
                  className={styles.fieldInput}
                  value={blockName}
                  onChange={(e) => { setBlockName(e.target.value); setApiError(null); }}
                  placeholder="e.g. Morning maintenance"
                  autoFocus
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="unified-block-type">Block type</label>
                <select
                  id="unified-block-type"
                  className={`${styles.fieldInput} ${styles.select}`}
                  value={blockTypeId}
                  onChange={(e) => setBlockTypeId(e.target.value)}
                >
                  {blockTypes.length === 0 ? (
                    <option value="" disabled>Loading…</option>
                  ) : (
                    <>
                      <option value="" disabled>Select a type…</option>
                      {blockTypes.map((bt) => (
                        <option key={bt.id} value={bt.id}>{bt.name}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="unified-block-start">Start time</label>
                <input
                  id="unified-block-start"
                  type="datetime-local"
                  className={styles.fieldInput}
                  value={blockStartTime}
                  onChange={(e) => setBlockStartTime(e.target.value)}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="unified-block-end">End time</label>
                <input
                  id="unified-block-end"
                  type="datetime-local"
                  className={styles.fieldInput}
                  value={blockEndTime}
                  onChange={(e) => setBlockEndTime(e.target.value)}
                />
              </div>
            </>
          )}
      </div>

      <div className={styles.modalFooter}>
        {/* Hide save button when booking warnings are shown so user must close manually */}
        {bookingWarnings.length === 0 && (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={saving || teeTimeDuplicate || bookingNoTeeTime}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// CalendarGrid
// ---------------------------------------------------------------------------

function CalendarGrid({
  teeTimes,
  bookings,
  blocks,
  blockTypes,
  gapMins,
  nineHolePace,
  currentDate,
  isToday,
  selectedCourse,
  selectedHole,
  onTeeTimeSaved,
  onBookingsSaved,
  onBlocksSaved,
  addToast,
  user,
}) {
  const gridRef = useRef(null);
  const eventAreaRef = useRef(null);

  // Tee time drag state
  const dragStateRef = useRef(null);
  const [dragInfo, setDragInfo] = useState(null);

  // Booking drag state
  const bookingDragStateRef = useRef(null);
  const [bookingDragInfo, setBookingDragInfo] = useState(null);

  // Block drag state
  const blockDragStateRef = useRef(null);
  const [blockDragInfo, setBlockDragInfo] = useState(null); // { block, ghostY, ghostH, newStartMins, newEndMins, dragType: 'move'|'resize-top'|'resize-bottom' }
  // Cooldown refs: true for 300 ms after a real drag ends, suppresses
  // accidental event-area clicks during that window.
  const blockDragJustEndedRef = useRef(false);
  const teeDragJustEndedRef = useRef(false);
  const bookingDragJustEndedRef = useRef(false);

  // Block conflict modal
  const [blockConflict, setBlockConflict] = useState(null); // { bookings: string[], pendingAction: fn }
  const [blockConflictConfirming, setBlockConflictConfirming] = useState(false);

  // Modals
  const [detailTeeTime, setDetailTeeTime] = useState(null);         // tee time detail modal
  const [newBookingTeeTime, setNewBookingTeeTime] = useState(null); // new booking modal
  const [detailBooking, setDetailBooking] = useState(null);         // booking detail modal
  const [detailBlock, setDetailBlock] = useState(null);             // block detail modal
  const [unifiedCreateData, setUnifiedCreateData] = useState(null); // { slotHour, slotMinute, existingTeeTime } | null

  const [nowY, setNowY] = useState(null);

  const layout = useMemo(
    () => buildLayout(teeTimes, gapMins),
    [teeTimes, gapMins]
  );
  const { compressed, intervalH, intervalMins, totalH } = layout;

  // Auto-scroll to ~6 AM on mount / date change
  useEffect(() => {
    if (!gridRef.current) return;
    const targetY = timeToY(6, 0, intervalMins, intervalH);
    gridRef.current.scrollTop = Math.max(0, targetY - 60);
  }, [currentDate, intervalMins, intervalH]);

  // Update now-line every minute
  useEffect(() => {
    if (!isToday) { setNowY(null); return; }
    const update = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      if (h >= START_HOUR && h < END_HOUR) {
        setNowY(timeToY(h, m, intervalMins, intervalH));
      } else {
        setNowY(null);
      }
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [isToday, intervalMins, intervalH]);

  useEffect(() => {
    if (!isToday) return;
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    if (h >= START_HOUR && h < END_HOUR) {
      setNowY(timeToY(h, m, intervalMins, intervalH));
    }
  }, [intervalMins, intervalH, isToday]);

  // Grid lines
  const gridLines = useMemo(() => {
    const totalMins = (END_HOUR - START_HOUR) * 60;
    const minsSet = new Set();

    // Interval-stepped lines
    for (let mins = 0; mins <= totalMins; mins += intervalMins) {
      minsSet.add(mins);
    }
    // Always include hour boundaries so they're never skipped by uneven intervals
    for (let mins = 0; mins <= totalMins; mins += 60) {
      minsSet.add(mins);
    }

    return [...minsSet].sort((a, b) => a - b).map((mins) => {
      const h = START_HOUR + Math.floor(mins / 60);
      const m = mins % 60;
      const y = timeToY(h, m, intervalMins, intervalH);
      const isHour = m === 0;
      return { y, h, m, isHour };
    });
  }, [intervalMins, intervalH]);

  // Tee time conflict detection
  const hasTeeConflict = useCallback(
    (targetHour, targetMin, draggedId) => {
      for (const tt of teeTimes) {
        if (tt.id === draggedId) continue;
        const { hour, min } = parseISOTime(tt.start_time);
        if (hour === targetHour && min === targetMin) return true;
      }
      return false;
    },
    [teeTimes]
  );

  // ---- Tee time tile drag ----
  const handleTileMouseDown = useCallback(
    (e, tt) => {
      if (blockDragJustEndedRef.current) return;
      // Only fire on the tile body, not the pencil icon button
      if (e.target.closest('button')) return;
      if (e.button !== 0) return;
      e.preventDefault();

      const startX = e.clientX;
      const startY = e.clientY;
      const startMs = Date.now();
      const { hour: origHour, min: origMin } = parseISOTime(tt.start_time);

      dragStateRef.current = {
        tt, origHour, origMin, startX, startY, startMs,
        isDragging: false, currentHour: origHour, currentMin: origMin,
      };

      const onMouseMove = (ev) => {
        const ds = dragStateRef.current;
        if (!ds) return;
        if (!ds.isDragging) {
          const dx = ev.clientX - ds.startX;
          const dy = ev.clientY - ds.startY;
          if (Math.sqrt(dx * dx + dy * dy) < 4) return;
          ds.isDragging = true;
        }
        const areaRect = eventAreaRef.current.getBoundingClientRect();
        const rawY = ev.clientY - areaRect.top;
        const snappedRowIndex = Math.round(rawY / intervalH);
        const snappedY = snappedRowIndex * intervalH;
        const { hour, minute } = yToTime(snappedY, intervalMins, intervalH);
        const outOfRange = hour < START_HOUR || hour >= END_HOUR;
        const conflict = !outOfRange && hasTeeConflict(hour, minute, ds.tt.id);
        ds.currentHour = hour;
        ds.currentMin = minute;
        setDragInfo({
          tt: ds.tt, draggedId: ds.tt.id,
          ghostX: ev.clientX, ghostY: ev.clientY,
          dropY: snappedY, dropH: Math.max(intervalH - 3, 14),
          hour, minute,
          valid: !outOfRange && !conflict,
          unchanged: hour === ds.origHour && minute === ds.origMin,
        });
      };

      const onMouseUp = async () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        const ds = dragStateRef.current;
        dragStateRef.current = null;
        setDragInfo(null);
        if (!ds) return;
        const elapsed = Date.now() - ds.startMs;
        if (!ds.isDragging || elapsed < DRAG_THRESHOLD_MS) {
          // Click → open tee time detail modal
          setDetailTeeTime(ds.tt);
          return;
        }
        const { currentHour: h, currentMin: m } = ds;
        const outOfRange = h < START_HOUR || h >= END_HOUR;
        const conflict = !outOfRange && hasTeeConflict(h, m, ds.tt.id);
        if (outOfRange || conflict) {
          addToast('Cannot move here — slot already occupied.', 'error');
          return;
        }
        teeDragJustEndedRef.current = true;
        setTimeout(() => { teeDragJustEndedRef.current = false; }, 300);
        if (h === ds.origHour && m === ds.origMin) return;
        try {
          const iso = buildISODatetime(currentDate, h, m);
          await updateTeeTime(ds.tt.id, { start_time: iso });
          addToast(`Tee time moved to ${formatHourMin(h, m)}.`);
          onTeeTimeSaved();
        } catch (err) {
          addToast(err.message || 'Failed to move tee time.', 'error');
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [intervalH, intervalMins, hasTeeConflict, currentDate, addToast, onTeeTimeSaved]
  );

  // ---- Booking tile drag ----
  const handleBookingMouseDown = useCallback(
    (e, booking) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startY = e.clientY;
      const startMs = Date.now();

      // Compute the tee time Y the booking is currently on
      const sourceTeeTime = teeTimes.find((tt) => tt.id === booking.tee_time_id) || null;

      bookingDragStateRef.current = {
        booking, startX, startY, startMs,
        isDragging: false,
        sourceTeeTime,
        targetTeeTime: null,
      };

      const onMouseMove = (ev) => {
        const ds = bookingDragStateRef.current;
        if (!ds) return;
        if (!ds.isDragging) {
          const dx = ev.clientX - ds.startX;
          const dy = ev.clientY - ds.startY;
          if (Math.sqrt(dx * dx + dy * dy) < 4) return;
          ds.isDragging = true;
        }

        const areaRect = eventAreaRef.current.getBoundingClientRect();
        const rawY = ev.clientY - areaRect.top;

        // Find the tee time nearest to the cursor Y
        let nearestTT = null;
        let nearestDist = Infinity;
        for (const tt of teeTimes) {
          const { hour, min } = parseISOTime(tt.start_time);
          const ttY = timeToY(hour, min, intervalMins, intervalH);
          const dist = Math.abs(rawY - ttY);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearestTT = tt;
          }
        }

        // Valid drop = found a tee time within range that isn't the source
        const threshold = intervalH * 0.75;
        const inRange = nearestTT !== null && nearestDist <= threshold;
        const isSource = inRange && nearestTT.id === ds.sourceTeeTime?.id;
        const valid = inRange && !isSource;
        ds.targetTeeTime = inRange ? nearestTT : null;

        let dropY = rawY;
        if (nearestTT) {
          const { hour, min } = parseISOTime(nearestTT.start_time);
          dropY = timeToY(hour, min, intervalMins, intervalH);
        }

        setBookingDragInfo({
          booking: ds.booking,
          draggedId: ds.booking.id,
          ghostX: ev.clientX,
          ghostY: ev.clientY,
          dropY,
          dropH: Math.max(intervalH - 3, 14),
          targetTeeTime: nearestTT,
          isSource,
          valid,
        });
      };

      const onMouseUp = async () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        const ds = bookingDragStateRef.current;
        bookingDragStateRef.current = null;
        setBookingDragInfo(null);
        if (!ds) return;

        const elapsed = Date.now() - ds.startMs;
        if (!ds.isDragging || elapsed < DRAG_THRESHOLD_MS) {
          // Click → open booking detail
          setDetailBooking(ds.booking);
          return;
        }
        if (!ds.targetTeeTime || ds.targetTeeTime.id === ds.sourceTeeTime?.id) return;

        bookingDragJustEndedRef.current = true;
        setTimeout(() => { bookingDragJustEndedRef.current = false; }, 300);

        try {
          await updateBooking(ds.booking.id, { tee_time_id: ds.targetTeeTime.id });
          const { hour, min } = parseISOTime(ds.targetTeeTime.start_time);
          addToast(`Booking moved to ${formatHourMin(hour, min)}.`);
          onBookingsSaved();
        } catch (err) {
          addToast(err.message || 'Failed to move booking.', 'error');
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [teeTimes, intervalH, intervalMins, addToast, onBookingsSaved]
  );

  // Compute booking layout: for each tee time, collect its bookings and divide width
  const bookingLayout = useMemo(() => {
    // Map teeTimeId → array of bookings
    const byTeeTime = {};
    for (const b of bookings) {
      if (!byTeeTime[b.tee_time_id]) byTeeTime[b.tee_time_id] = [];
      byTeeTime[b.tee_time_id].push(b);
    }
    const result = [];
    for (const [teeTimeId, bList] of Object.entries(byTeeTime)) {
      const tt = teeTimes.find((t) => t.id === Number(teeTimeId));
      if (!tt) continue;
      const { hour, min } = parseISOTime(tt.start_time);
      const top = timeToY(hour, min, intervalMins, intervalH) + 1;
      const height = Math.max(intervalH - 3, 14);
      const count = bList.length;
      bList.forEach((b, colIdx) => {
        const slotWidthPct = BOOKING_AREA_PCT / count;
        const leftPct = BOOKING_LEFT_PCT + colIdx * slotWidthPct;
        result.push({ booking: b, top, height, leftPct, widthPct: slotWidthPct - 0.5 });
      });
    }
    return result;
  }, [bookings, teeTimes, intervalMins, intervalH]);

  // Block layout computation
  const blockLayout = useMemo(() => {
    if (!blocks || blocks.length === 0) return [];
    return blocks.map((b) => {
      const { hour: sH, min: sMin } = parseISOTime(b.start_time || '');
      const { hour: eH, min: eMin } = parseISOTime(b.end_time || '');
      const startMins = sH * 60 + sMin;
      const endMins = eH * 60 + eMin;
      const top = timeToY(sH, sMin, intervalMins, intervalH);
      const bottom = timeToY(eH, eMin, intervalMins, intervalH);
      const height = Math.max(bottom - top, intervalH);
      return { block: b, top, height, startMins, endMins };
    });
  }, [blocks, intervalMins, intervalH]);

  // Compute set of tee time IDs that are muted (fall within a block range)
  const mutedTeeTimeIds = useMemo(() => {
    const muted = new Set();
    for (const { startMins, endMins } of blockLayout) {
      for (const tt of teeTimes) {
        const { hour, min } = parseISOTime(tt.start_time);
        const ttMins = hour * 60 + min;
        if (ttMins >= startMins && ttMins < endMins) {
          muted.add(tt.id);
        }
      }
    }
    return muted;
  }, [blockLayout, teeTimes]);

  // ---- Block drag / click ----
  const handleBlockMouseDown = useCallback(
    (e, block, dragType) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startY = e.clientY;
      const startMs = Date.now();

      const { hour: sH, min: sMin } = parseISOTime(block.start_time || '');
      const { hour: eH, min: eMin } = parseISOTime(block.end_time || '');
      const origStartMins = sH * 60 + sMin;
      const origEndMins = eH * 60 + eMin;
      const origDurationMins = origEndMins - origStartMins;

      blockDragStateRef.current = {
        block, dragType, startX, startY, startMs,
        isDragging: false,
        origStartMins, origEndMins,
        currentStartMins: origStartMins,
        currentEndMins: origEndMins,
      };

      const onMouseMove = (ev) => {
        const ds = blockDragStateRef.current;
        if (!ds) return;
        if (!ds.isDragging) {
          const dx = ev.clientX - ds.startX;
          const dy = ev.clientY - ds.startY;
          if (Math.sqrt(dx * dx + dy * dy) < 4) return;
          ds.isDragging = true;
        }

        const areaRect = eventAreaRef.current.getBoundingClientRect();
        const rawY = ev.clientY - areaRect.top;
        const dyFromStart = ev.clientY - ds.startY;

        let newStartMins, newEndMins;

        if (ds.dragType === 'move') {
          // Snap the delta to intervalMins
          const deltaMins = Math.round((dyFromStart / intervalH) * intervalMins / intervalMins) * intervalMins;
          newStartMins = Math.max(START_HOUR * 60, ds.origStartMins + deltaMins);
          newEndMins = newStartMins + origDurationMins;
        } else if (ds.dragType === 'resize-top') {
          const snappedY = Math.round(rawY / intervalH) * intervalH;
          const { hour, minute } = yToTime(snappedY, intervalMins, intervalH);
          newStartMins = hour * 60 + minute;
          newEndMins = ds.origEndMins;
          // Enforce minimum 1 interval
          if (newStartMins >= newEndMins - intervalMins) {
            newStartMins = newEndMins - intervalMins;
          }
        } else {
          // resize-bottom
          const snappedY = Math.round(rawY / intervalH) * intervalH;
          const { hour, minute } = yToTime(snappedY, intervalMins, intervalH);
          newEndMins = hour * 60 + minute;
          newStartMins = ds.origStartMins;
          // Enforce minimum 1 interval
          if (newEndMins <= newStartMins + intervalMins) {
            newEndMins = newStartMins + intervalMins;
          }
        }

        ds.currentStartMins = newStartMins;
        ds.currentEndMins = newEndMins;

        const ghostTop = timeToY(
          Math.floor(newStartMins / 60), newStartMins % 60, intervalMins, intervalH
        );
        const ghostBottom = timeToY(
          Math.floor(newEndMins / 60), newEndMins % 60, intervalMins, intervalH
        );

        setBlockDragInfo({
          block: ds.block,
          dragType: ds.dragType,
          ghostTop,
          ghostH: Math.max(ghostBottom - ghostTop, intervalH),
          newStartMins,
          newEndMins,
        });
      };

      const buildISOFromMins = (dateStr, totalMins) => {
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return buildISODatetime(dateStr, h, m);
      };

      const attemptBlockUpdate = async (newStartMins, newEndMins) => {
        const payload = {
          start_time: buildISOFromMins(currentDate, newStartMins),
          end_time: buildISOFromMins(currentDate, newEndMins),
        };
        try {
          await updateBlock(block.id, payload);
          addToast('Block updated.');
          onBlocksSaved();
        } catch (err) {
          const msg = err.message || '';
          let affected = null;
          try {
            const parsed = JSON.parse(msg);
            if (parsed.affected_bookings) affected = parsed.affected_bookings;
          } catch (_) {
            const match = msg.match(/affected_bookings["\s:]+\[([^\]]+)\]/);
            if (match) {
              affected = match[1].split(',').map((s) => s.trim().replace(/"/g, ''));
            }
          }
          if (affected && affected.length > 0) {
            setBlockConflict({
              bookings: affected,
              pendingAction: async () => {
                try {
                  await updateBlock(block.id, { ...payload, archive_booking_ids: affected });
                  addToast('Block updated.');
                  onBlocksSaved();
                } catch (retryErr) {
                  addToast(retryErr.message || 'Failed to update block.', 'error');
                }
              },
            });
          } else {
            addToast(msg || 'Failed to update block.', 'error', 10000);
          }
        }
      };

      const onMouseUp = async () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        const ds = blockDragStateRef.current;
        blockDragStateRef.current = null;
        setBlockDragInfo(null);
        if (!ds) return;

        const elapsed = Date.now() - ds.startMs;
        if (!ds.isDragging || elapsed < DRAG_THRESHOLD_MS) {
          // Click → open block detail
          setDetailBlock(ds.block);
          return;
        }

        // Real drag ended — suppress accidental clicks on tee tiles / event area
        blockDragJustEndedRef.current = true;
        setTimeout(() => { blockDragJustEndedRef.current = false; }, 300);

        const { currentStartMins, currentEndMins } = ds;
        if (currentStartMins === origStartMins && currentEndMins === origEndMins) return;

        await attemptBlockUpdate(currentStartMins, currentEndMins);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [intervalH, intervalMins, currentDate, addToast, onBlocksSaved]
  );

  const handleBlockConflictConfirm = useCallback(async () => {
    if (!blockConflict) return;
    setBlockConflictConfirming(true);
    try {
      await blockConflict.pendingAction();
    } finally {
      setBlockConflictConfirming(false);
      setBlockConflict(null);
    }
  }, [blockConflict]);

  const handleEventAreaClick = useCallback(
    (e, currentDate, selectedCourseId) => {
      if (blockDragJustEndedRef.current || teeDragJustEndedRef.current || bookingDragJustEndedRef.current) return;
      // Only fire when clicking directly on the event area or a grid rule — not on tiles or block bands
      const target = e.target;
      if (
        target.closest('[data-tee-tile]') ||
        target.closest('[data-booking-tile]') ||
        target.closest('[data-block-band]')
      ) return;

      const areaRect = eventAreaRef.current.getBoundingClientRect();
      const rawY = e.clientY - areaRect.top;
      const snappedRowIndex = Math.floor(rawY / intervalH);
      const snappedY = snappedRowIndex * intervalH;
      const { hour, minute } = yToTime(snappedY, intervalMins, intervalH);

      // Find if a tee time already exists at this exact slot
      const existingTeeTime = teeTimes.find((tt) => {
        const { hour: h, min: m } = parseISOTime(tt.start_time);
        return h === hour && m === minute;
      }) || null;

      setUnifiedCreateData({ slotHour: hour, slotMinute: minute, existingTeeTime });
    },
    [intervalH, intervalMins, teeTimes]
  );

  const handleTeeTimeModalSaved = useCallback(
    (message) => {
      addToast(message);
      onTeeTimeSaved();
    },
    [addToast, onTeeTimeSaved]
  );

  const handleTeeTimeNewBooking = useCallback((tt) => {
    setDetailTeeTime(null);
    setNewBookingTeeTime(tt);
  }, []);

  const handleBookingCreated = useCallback(
    (message) => {
      addToast(message);
      onBookingsSaved();
    },
    [addToast, onBookingsSaved]
  );

  const handleBookingArchived = useCallback(
    (message) => {
      addToast(message);
      onBookingsSaved();
    },
    [addToast, onBookingsSaved]
  );

  const handleBlockSaved = useCallback(
    (message) => {
      addToast(message);
      onBlocksSaved();
    },
    [addToast, onBlocksSaved]
  );

  const handleUnifiedTeeTimeCreated = useCallback((message) => {
    addToast(message);
    onTeeTimeSaved();
  }, [addToast, onTeeTimeSaved]);

  const handleUnifiedBookingCreated = useCallback((message) => {
    addToast(message);
    onBookingsSaved();
  }, [addToast, onBookingsSaved]);

  const handleUnifiedBlockCreated = useCallback((message) => {
    addToast(message);
    onBlocksSaved();
  }, [addToast, onBlocksSaved]);

  return (
    <>
      <div className={styles.calendarBody} ref={gridRef}>
        <div className={styles.calendarGrid}>
          {/* Time gutter */}
          <div className={styles.timeGutter} style={{ height: totalH }}>
            {gridLines.map(({ y, h, m, isHour }) => {
              if (isHour) {
                return (
                  <span
                    key={`hl-${h}`}
                    className={styles.hourLabel}
                    style={{ top: y }}
                    aria-hidden="true"
                  >
                    {h === 0 ? '12 AM' : h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`}
                  </span>
                );
              }
              if (!compressed || m % gapMins === 0) {
                return (
                  <span
                    key={`sl-${h}-${m}`}
                    className={styles.subLabel}
                    style={{ top: y }}
                    aria-hidden="true"
                  >
                    :{String(m).padStart(2, '0')}
                  </span>
                );
              }
              return null;
            })}
          </div>

          {/* Event area */}
          <div
            className={styles.eventArea}
            ref={eventAreaRef}
            style={{ height: totalH }}
            role="grid"
            aria-label="Tee time calendar"
            onClick={(e) => handleEventAreaClick(e, currentDate, selectedCourse?.id)}
          >
            {/* Grid lines */}
            {gridLines.map(({ y, h, m, isHour }) => (
              <div
                key={`rule-${h}-${m}`}
                className={isHour ? styles.hourRule : styles.subRule}
                style={{ top: y }}
                aria-hidden="true"
              />
            ))}

            {/* Block bands (z-index 1, below tee/booking tiles at z-index 3) */}
            {blockLayout.map(({ block, top, height }) => (
              <div
                key={block.id}
                className={[
                  styles.blockBand,
                  blockDragInfo?.block?.id === block.id ? styles.blockBandDragging : '',
                ].join(' ').trim()}
                style={{
                  top,
                  height,
                  position: 'absolute',
                  left: 0,
                  right: 0,
                }}
                data-block-band="true"
                onMouseDown={(e) => {
                  // Determine if on top/bottom resize handle or body
                  const rect = e.currentTarget.getBoundingClientRect();
                  const relY = e.clientY - rect.top;
                  let dragType = 'move';
                  if (relY <= 8) dragType = 'resize-top';
                  else if (relY >= rect.height - 8) dragType = 'resize-bottom';
                  handleBlockMouseDown(e, block, dragType);
                }}
                role="button"
                tabIndex={0}
                aria-label={`Block: ${block.name}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDetailBlock(block);
                  }
                }}
              >
                {/* Top resize handle */}
                <div
                  className={styles.blockBandResizeHandle}
                  style={{ top: 0 }}
                  aria-hidden="true"
                />
                {/* Label */}
                <span className={styles.blockBandLabel} aria-hidden="true">
                  {block.name}
                  {block.block_type_name ? ` · ${block.block_type_name}` : ''}
                </span>
                {/* Bottom resize handle */}
                <div
                  className={styles.blockBandResizeHandle}
                  style={{ bottom: 0, top: 'auto' }}
                  aria-hidden="true"
                />
              </div>
            ))}

            {/* Now line */}
            {nowY !== null && (
              <div
                className={styles.nowLine}
                style={{ top: nowY }}
                role="presentation"
                aria-label="Current time"
              >
                <div className={styles.nowDot} />
              </div>
            )}

            {/* Tee time tiles */}
            {teeTimes.map((tt) => {
              const { hour, min } = parseISOTime(tt.start_time);
              const top = timeToY(hour, min, intervalMins, intervalH) + 1;
              const height = Math.max(intervalH - 3, 14);
              const fontSize = height < 16 ? 9 : 11;
              const isMuted = mutedTeeTimeIds.has(tt.id);

              return (
                <div
                  key={tt.id}
                  className={[
                    styles.teeTile,
                    dragInfo?.draggedId === tt.id ? styles.teeTileDragging : '',
                    isMuted ? styles.teeTileMuted : '',
                  ].join(' ').trim()}
                  style={{
                    top,
                    height,
                    left: TEE_LEFT,
                    width: `calc(${TEE_PCT}% - ${TEE_LEFT}px)`,
                    fontSize,
                    position: 'absolute',
                  }}
                  data-tee-tile="true"
                  onMouseDown={(e) => handleTileMouseDown(e, tt)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Tee time at ${formatHourMin(hour, min)} — click to view details`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDetailTeeTime(tt);
                    }
                  }}
                >
                  <span className={styles.teeTileLabel}>
                    {formatHourMin(hour, min)}
                  </span>
                </div>
              );
            })}

            {/* Booking tiles */}
            {bookingLayout.map(({ booking, top, height, leftPct, widthPct }) => {
              const isDraggingThis = bookingDragInfo?.draggedId === booking.id;
              const fontSize = height < 16 ? 9 : 11;
              const showDetail = height >= 28;

              // Squish tiles on the hover target to make room for the drop zone
              let effectiveLeftPct = leftPct;
              let effectiveWidthPct = widthPct;
              if (
                bookingDragInfo?.valid &&
                bookingDragInfo.targetTeeTime?.id === booking.tee_time_id &&
                !isDraggingThis
              ) {
                const siblingsOnTarget = bookingLayout.filter(
                  (bl) =>
                    bl.booking.tee_time_id === bookingDragInfo.targetTeeTime.id &&
                    bl.booking.id !== bookingDragInfo.draggedId
                );
                const colIdx = siblingsOnTarget.findIndex((bl) => bl.booking.id === booking.id);
                const N = siblingsOnTarget.length;
                const newSlotW = BOOKING_AREA_PCT / (N + 1);
                effectiveLeftPct = BOOKING_LEFT_PCT + colIdx * newSlotW;
                effectiveWidthPct = newSlotW - 0.5;
              }

              return (
                <div
                  key={booking.id}
                  className={[
                    styles.bookingTile,
                    isDraggingThis ? styles.bookingTileDragging : '',
                    booking.is_problematic ? styles.bookingTileProblematic : '',
                  ].join(' ').trim()}
                  style={{
                    top,
                    height,
                    left: `${effectiveLeftPct}%`,
                    width: `${effectiveWidthPct}%`,
                    fontSize,
                    position: 'absolute',
                  }}
                  data-booking-tile="true"
                  onMouseDown={(e) => handleBookingMouseDown(e, booking)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Booking ${booking.booking_number} — ${booking.booked_by_name || ''}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDetailBooking(booking);
                    }
                  }}
                >

                  <span className={styles.bookingTileNumber}>
                    {booking.booking_number}
                  </span>
                  {showDetail && (
                    <>
                      <span className={styles.bookingTileName}>
                        {booking.booked_by_name}
                      </span>
                      <span className={styles.bookingTilePlayers}>
                        {booking.players?.length === 1
                          ? '1 player'
                          : `${booking.players?.length || 0} players`}
                      </span>
                    </>
                  )}
                </div>
              );
            })}

            {/* Tee time drop zone during tee time drag */}
            {dragInfo && (
              <div
                className={
                  dragInfo.valid
                    ? `${styles.dropZone} ${styles.dropZoneValid}`
                    : `${styles.dropZone} ${styles.dropZoneInvalid}`
                }
                style={{
                  top: dragInfo.dropY + 1,
                  height: dragInfo.dropH,
                  left: TEE_LEFT,
                  width: `calc(${TEE_PCT}% - ${TEE_LEFT}px)`,
                  position: 'absolute',
                }}
                aria-hidden="true"
              />
            )}

            {/* Booking drop zone during booking drag */}
            {bookingDragInfo && bookingDragInfo.targetTeeTime && !bookingDragInfo.isSource && (() => {
              const siblingsOnTarget = bookingLayout.filter(
                (bl) =>
                  bl.booking.tee_time_id === bookingDragInfo.targetTeeTime.id &&
                  bl.booking.id !== bookingDragInfo.draggedId
              );
              const N = siblingsOnTarget.length;
              const slotW = BOOKING_AREA_PCT / (N + 1);
              const dropZoneLeft = BOOKING_LEFT_PCT + N * slotW;
              const dropZoneWidth = slotW - 0.5;
              return (
                <div
                  className={
                    bookingDragInfo.valid
                      ? styles.bookingDropZoneValid
                      : styles.bookingDropZoneInvalid
                  }
                  style={{
                    top: bookingDragInfo.dropY + 1,
                    height: bookingDragInfo.dropH,
                    left: `${dropZoneLeft}%`,
                    width: `${dropZoneWidth}%`,
                    position: 'absolute',
                  }}
                  aria-hidden="true"
                />
              );
            })()}
          </div>
        </div>
      </div>

      {/* Tee time drag ghost */}
      {dragInfo && dragInfo.tt &&
        createPortal(
          <div
            className={styles.dragGhost}
            style={{
              left: dragInfo.ghostX + 8,
              top: dragInfo.ghostY - 10,
              height: Math.max(dragInfo.dropH, 24),
              minWidth: 80,
              fontSize: dragInfo.dropH < 16 ? 9 : 11,
            }}
            aria-hidden="true"
          >
            {formatHourMin(dragInfo.hour, dragInfo.minute)}
          </div>,
          document.body
        )
      }

      {/* Booking drag ghost */}
      {bookingDragInfo && bookingDragInfo.booking &&
        createPortal(
          <div
            className={styles.bookingGhost}
            style={{
              left: bookingDragInfo.ghostX + 8,
              top: bookingDragInfo.ghostY - 10,
              height: Math.max(bookingDragInfo.dropH, 24),
            }}
            aria-hidden="true"
          >
            {bookingDragInfo.booking.booking_number}
          </div>,
          document.body
        )
      }

      {/* Block drag ghost (fixed, rendered via portal) */}
      {blockDragInfo && eventAreaRef.current &&
        (() => {
          const areaRect = eventAreaRef.current.getBoundingClientRect();
          return createPortal(
            <div
              className={styles.blockGhost}
              style={{
                left: areaRect.left,
                top: areaRect.top + blockDragInfo.ghostTop,
                width: areaRect.width,
                height: blockDragInfo.ghostH,
              }}
              aria-hidden="true"
            >
              <span className={styles.blockBandLabel}>
                {blockDragInfo.block.name}
                {' '}
                {formatHourMin(
                  Math.floor(blockDragInfo.newStartMins / 60),
                  blockDragInfo.newStartMins % 60
                )}
                {' – '}
                {formatHourMin(
                  Math.floor(blockDragInfo.newEndMins / 60),
                  blockDragInfo.newEndMins % 60
                )}
              </span>
            </div>,
            document.body
          );
        })()
      }

      {/* Tee time detail modal (tile click) */}
      {detailTeeTime && (
        <TeeTimeDetailModal
          teeTime={detailTeeTime}
          gapMins={gapMins}
          teeTimes={teeTimes}
          selectedCourse={selectedCourse}
          onClose={() => setDetailTeeTime(null)}
          onSaved={handleTeeTimeModalSaved}
          onNewBooking={() => handleTeeTimeNewBooking(detailTeeTime)}
          addToast={addToast}
        />
      )}

      {/* New booking modal (from tee time detail modal) */}
      {newBookingTeeTime && (
        <NewBookingModal
          teeTime={newBookingTeeTime}
          selectedCourse={selectedCourse}
          onClose={() => setNewBookingTeeTime(null)}
          onCreated={handleBookingCreated}
        />
      )}

      {/* Booking detail modal */}
      {detailBooking && (
        <BookingDetailModal
          booking={detailBooking}
          nineHolePace={nineHolePace}
          onClose={() => setDetailBooking(null)}
          onArchived={handleBookingArchived}
          onUpdated={onBookingsSaved}
          addToast={addToast}
          user={user}
        />
      )}

      {/* Block detail modal */}
      {detailBlock && (
        <BlockDetailModal
          block={detailBlock}
          blockTypes={blockTypes}
          onClose={() => setDetailBlock(null)}
          onArchived={handleBlockSaved}
          onSaved={handleBlockSaved}
          addToast={addToast}
        />
      )}

      {/* Unified create modal (from event area click) */}
      {unifiedCreateData && (
        <UnifiedCreateModal
          slotHour={unifiedCreateData.slotHour}
          slotMinute={unifiedCreateData.slotMinute}
          existingTeeTime={unifiedCreateData.existingTeeTime}
          teeTimes={teeTimes}
          gapMins={gapMins}
          blockTypes={blockTypes}
          selectedCourseId={selectedCourse?.id}
          selectedHole={selectedHole}
          selectedCourse={selectedCourse}
          currentDate={currentDate}
          onClose={() => setUnifiedCreateData(null)}
          onTeeTimeCreated={handleUnifiedTeeTimeCreated}
          onBookingCreated={handleUnifiedBookingCreated}
          onBlockCreated={handleUnifiedBlockCreated}
          addToast={addToast}
        />
      )}

      {/* Block conflict confirmation modal */}
      {blockConflict && (
        <BookingConflictModal
          affectedBookings={blockConflict.bookings}
          onConfirm={handleBlockConflictConfirm}
          onCancel={() => setBlockConflict(null)}
          confirming={blockConflictConfirming}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// CalendarPage (main)
// ---------------------------------------------------------------------------

export default function CalendarPage({ user }) {
  const todayStr = toDateString(new Date());
  const [currentDate, setCurrentDate] = useState(todayStr);
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedHole, setSelectedHole] = useState(1);
  const [gapMins, setGapMins] = useState(DEFAULT_GAP);
  const [nineHolePace, setNineHolePace] = useState(60);
  const [teeTimes, setTeeTimes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [blockTypes, setBlockTypes] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [error, setError] = useState(null);
  const { toasts, addToast } = useToasts();

  const selectedCourse = useMemo(
    () => courses.find((c) => c.id === selectedCourseId) || null,
    [courses, selectedCourseId]
  );

  const holeOptions = useMemo(() => {
    if (!selectedCourse) return [1];
    return selectedCourse.holes === 9 ? [1] : [1, 10];
  }, [selectedCourse]);

  const isToday = currentDate === todayStr;

  const layout = useMemo(
    () => buildLayout(teeTimes, gapMins),
    [teeTimes, gapMins]
  );

  // Fetch courses + gap setting + block types on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      setLoadingCourses(true);
      setError(null);
      try {
        const [coursesData, gapData, nineHolePaceData, blockTypesData] = await Promise.allSettled([
          listAllGolfCourses(),
          getSetting('gap'),
          getSetting('nine_hole_pace'),
          listBlockTypes(),
        ]);
        if (cancelled) return;
        if (coursesData.status === 'fulfilled') {
          const items = coursesData.value.items || [];
          setCourses(items);
          if (items.length > 0) setSelectedCourseId(items[0].id);
        } else {
          setError('Failed to load golf courses.');
        }
        if (gapData.status === 'fulfilled') {
          const val = parseInt(gapData.value?.setting?.value, 10);
          if (!isNaN(val) && val > 0) setGapMins(val);
        }
        if (nineHolePaceData.status === 'fulfilled') {
          const val = parseInt(nineHolePaceData.value?.setting?.value, 10);
          if (!isNaN(val) && val > 0) setNineHolePace(val);
        }
        if (blockTypesData.status === 'fulfilled') {
          setBlockTypes(blockTypesData.value.items || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load calendar data.');
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    }
    init();
    return () => { cancelled = true; };
  }, []);

  // Fetch tee times + bookings when date / course / hole changes
  useEffect(() => {
    if (!selectedCourseId) return;
    let cancelled = false;

    async function fetchAll() {
      setLoadingTimes(true);
      try {
        const [timesData, bookingsData, blocksData] = await Promise.allSettled([
          listTeeTimes({ date: currentDate, golfCourseId: selectedCourseId, hole: selectedHole }),
          listBookings({ date: currentDate, isCancelled: false }),
          listBlocks({ date: currentDate, golfCourseId: selectedCourseId }),
        ]);
        if (cancelled) return;

        if (timesData.status === 'fulfilled') {
          setTeeTimes(timesData.value.items || []);
        } else {
          setTeeTimes([]);
          addToast(timesData.reason?.message || 'Failed to load tee times.', 'error');
        }

        if (bookingsData.status === 'fulfilled') {
          // Filter to bookings belonging to the selected course
          const allBookings = bookingsData.value.items || [];
          const filtered = allBookings.filter(
            (b) => b.tee_time?.golf_course_id === selectedCourseId
          );
          setBookings(filtered);
        } else {
          setBookings([]);
          // Booking fetch failure is non-fatal; don't block calendar
        }

        if (blocksData.status === 'fulfilled') {
          setBlocks(blocksData.value.items || []);
        } else {
          setBlocks([]);
        }
      } finally {
        if (!cancelled) setLoadingTimes(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [currentDate, selectedCourseId, selectedHole, addToast]);

  // Reset hole when course changes
  useEffect(() => {
    if (selectedCourse && selectedCourse.holes === 9 && selectedHole === 10) {
      setSelectedHole(1);
    }
  }, [selectedCourse, selectedHole]);

  const goToPrevDay = useCallback(() => {
    const d = new Date(currentDate + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    setCurrentDate(toDateString(d));
  }, [currentDate]);

  const goToNextDay = useCallback(() => {
    const d = new Date(currentDate + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    setCurrentDate(toDateString(d));
  }, [currentDate]);

  const goToToday = useCallback(() => {
    setCurrentDate(toDateString(new Date()));
  }, []);

  const handleDatePickerChange = useCallback((e) => {
    if (e.target.value) setCurrentDate(e.target.value);
  }, []);

  const handleCourseChange = useCallback((e) => {
    const id = parseInt(e.target.value, 10);
    setSelectedCourseId(id);
    setSelectedHole(1);
  }, []);

  const handleHoleChange = useCallback((e) => {
    setSelectedHole(parseInt(e.target.value, 10));
  }, []);

  // Refresh tee times, bookings, and blocks
  const refreshAll = useCallback(() => {
    if (!selectedCourseId) return;
    Promise.allSettled([
      listTeeTimes({ date: currentDate, golfCourseId: selectedCourseId, hole: selectedHole }),
      listBookings({ date: currentDate, isCancelled: false }),
      listBlocks({ date: currentDate, golfCourseId: selectedCourseId }),
    ]).then(([timesResult, bookingsResult, blocksResult]) => {
      if (timesResult.status === 'fulfilled') {
        setTeeTimes(timesResult.value.items || []);
      }
      if (bookingsResult.status === 'fulfilled') {
        const allBookings = bookingsResult.value.items || [];
        setBookings(allBookings.filter((b) => b.tee_time?.golf_course_id === selectedCourseId));
      }
      if (blocksResult.status === 'fulfilled') {
        setBlocks(blocksResult.value.items || []);
      }
    }).catch((err) => {
      addToast(err.message || 'Failed to refresh calendar.', 'error');
    });
  }, [currentDate, selectedCourseId, selectedHole, addToast]);

  // Refresh only tee times (after drag/move)
  const handleTeeTimeSaved = useCallback(() => {
    if (!selectedCourseId) return;
    listTeeTimes({ date: currentDate, golfCourseId: selectedCourseId, hole: selectedHole })
      .then((data) => setTeeTimes(data.items || []))
      .catch((err) => addToast(err.message || 'Failed to refresh tee times.', 'error'));
  }, [currentDate, selectedCourseId, selectedHole, addToast]);

  const displayDate = useMemo(() => {
    const d = new Date(currentDate + 'T00:00:00');
    return formatDateLong(d);
  }, [currentDate]);

  // Calendar content
  let calendarContent;
  if (loadingCourses) {
    calendarContent = <div className={styles.stateContainer}>Loading calendar…</div>;
  } else if (error) {
    calendarContent = <div className={`${styles.stateContainer} ${styles.stateError}`}>{error}</div>;
  } else if (!selectedCourseId) {
    calendarContent = <div className={styles.stateContainer}>No golf courses found. Add a course to get started.</div>;
  } else if (loadingTimes) {
    calendarContent = <div className={styles.stateContainer}>Loading tee times…</div>;
  } else {
    calendarContent = (
      <CalendarGrid
        teeTimes={teeTimes}
        bookings={bookings}
        blocks={blocks}
        blockTypes={blockTypes}
        gapMins={gapMins}
        nineHolePace={nineHolePace}
        currentDate={currentDate}
        isToday={isToday}
        selectedCourse={selectedCourse}
        selectedHole={selectedHole}
        onTeeTimeSaved={handleTeeTimeSaved}
        onBookingsSaved={refreshAll}
        onBlocksSaved={refreshAll}
        addToast={addToast}
        user={user}
      />
    );
  }

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar} role="toolbar" aria-label="Calendar controls">
        <button
          type="button"
          className={styles.navBtn}
          onClick={goToPrevDay}
          aria-label="Previous day"
        >
          <ChevronLeft size={16} strokeWidth={1.5} />
        </button>

        <button
          type="button"
          className={styles.navBtn}
          onClick={goToNextDay}
          aria-label="Next day"
        >
          <ChevronRight size={16} strokeWidth={1.5} />
        </button>

        <button
          type="button"
          className={styles.todayBtn}
          onClick={goToToday}
          aria-label="Go to today"
        >
          Today
        </button>

        <span className={styles.dateDisplay}>{displayDate}</span>

        <input
          type="date"
          className={styles.datePicker}
          value={currentDate}
          onChange={handleDatePickerChange}
          aria-label="Jump to date"
        />

        <div className={styles.toolbarSep} aria-hidden="true" />

        <label className={styles.toolbarLabel} htmlFor="course-select">
          Course
        </label>
        <select
          id="course-select"
          className={styles.select}
          value={selectedCourseId || ''}
          onChange={handleCourseChange}
          disabled={loadingCourses || courses.length === 0}
        >
          {courses.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
          {courses.length === 0 && <option value="" disabled>No courses</option>}
        </select>

        <label className={styles.toolbarLabel} htmlFor="hole-select">
          Hole
        </label>
        <select
          id="hole-select"
          className={styles.select}
          value={selectedHole}
          onChange={handleHoleChange}
          disabled={loadingCourses || !selectedCourse}
        >
          {holeOptions.map((h) => (
            <option key={h} value={h}>Hole {h}</option>
          ))}
        </select>

        <div className={styles.toolbarSpacer} />

        {/* Legend */}
        <div className={styles.legend} aria-label="Legend">
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.legendSwatchTee}`} aria-hidden="true" />
            Tee time
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.legendSwatchBooking}`} aria-hidden="true" />
            Booking
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.legendSwatchBlock}`} aria-hidden="true" />
            Block
          </span>
        </div>

      </div>

      {/* Compressed-view banner */}
      {layout.compressed && !loadingTimes && !loadingCourses && !error && (
        <div className={styles.compressedBanner} role="alert">
          <AlertTriangle
            size={14}
            strokeWidth={1.5}
            className={styles.compressedBannerIcon}
            aria-hidden="true"
          />
          Some tee times are closer together than the {gapMins}-minute gap
          setting. Calendar is in compressed view.
        </div>
      )}

      {/* Calendar body */}
      {calendarContent}

      {/* Toast notifications */}
      {toasts.length > 0 &&
        createPortal(
          <div className={styles.toastContainer} aria-live="polite">
            {toasts.map((t) => (
              <div
                key={t.id}
                className={[
                  styles.toast,
                  t.type === 'error' ? styles.toastError : '',
                ].join(' ').trim()}
                role="status"
              >
                {t.message}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
